// API wrappers for the Forms feature: form CRUD (the builder), the
// submissions inbox (filters / read flags / delete) and filtered exports.
import type { ApiClient } from "./api";

export type FormFieldType =
  | "text" | "textarea" | "email" | "number" | "select" | "radio"
  | "checkbox" | "date" | "time" | "file" | "consent";

export interface FormField {
  id?: string;
  type: FormFieldType;
  label: string;
  required: boolean;
  placeholder: string;
  defaultValue: string;
  helpText: string;
  cssClass: string;
  options?: string[];
  min?: number | null;
  max?: number | null;
  maxLength?: number | null;
  accept?: string[];
  maxSizeMb?: number | null;
}

export interface FormSettings {
  submitLabel: string;
  successMessage: string;
  redirectUrl: string | null;
  notifyEmail: string | null;
  retentionDays: number | null;
}

export interface Form {
  id: string;
  name: string;
  fields: FormField[];
  settings: FormSettings;
  created_at?: string;
  updated_at?: string;
  submission_count?: number;
}

export interface FormsResponse {
  forms: Form[];
  /** Public R2 base URL for linking uploaded files; null when unresolved. */
  assetsBaseUrl: string | null;
}

export interface SubmissionFilters {
  formId?: string;
  from?: string;
  to?: string;
  q?: string;
}

export interface Submission {
  id: number;
  form_id: string;
  data: Record<string, unknown>;
  created_at: string;
  read: boolean;
}

export interface SubmissionsResponse {
  submissions: Submission[];
  page: number;
  per_page: number;
  total: number;
  unread: number;
}

export async function fetchForms(api: ApiClient): Promise<FormsResponse> {
  const data = await api.request<{ forms?: Form[]; assets_base_url?: string | null }>(
    "/tenant/forms"
  );
  return { forms: data.forms ?? [], assetsBaseUrl: data.assets_base_url ?? null };
}

export function createForm(api: ApiClient, name: string): Promise<Form> {
  return api.request("/tenant/forms", {
    method: "POST",
    body: JSON.stringify({ name })
  });
}

export function fetchForm(api: ApiClient, formId: string): Promise<Form> {
  return api.request(`/tenant/forms/${formId}`);
}

export function updateForm(
  api: ApiClient,
  formId: string,
  payload: { name: string; fields: FormField[]; settings: FormSettings }
): Promise<Form> {
  return api.request(`/tenant/forms/${formId}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export function deleteForm(api: ApiClient, formId: string): Promise<unknown> {
  return api.request(`/tenant/forms/${formId}`, { method: "DELETE" });
}

function filterParams(filters: SubmissionFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.formId) params.set("form_id", filters.formId);
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.q) params.set("q", filters.q);
  return params;
}

export function fetchSubmissions(
  api: ApiClient,
  { page = 1, perPage = 20, ...filters }: SubmissionFilters & { page?: number; perPage?: number } = {}
): Promise<SubmissionsResponse> {
  const params = filterParams(filters);
  params.set("page", String(page));
  params.set("per_page", String(perPage));
  return api.request(`/tenant/forms/submissions?${params}`);
}

export function setSubmissionRead(
  api: ApiClient,
  id: number,
  read: boolean
): Promise<unknown> {
  return api.request(`/tenant/forms/submissions/${id}/read`, {
    method: "PUT",
    body: JSON.stringify({ read })
  });
}

export function deleteSubmission(api: ApiClient, id: number): Promise<unknown> {
  return api.request(`/tenant/forms/submissions/${id}`, { method: "DELETE" });
}

export function deleteSubmissions(api: ApiClient, ids: number[]): Promise<unknown> {
  return api.request("/tenant/forms/submissions/delete", {
    method: "POST",
    body: JSON.stringify({ ids })
  });
}

/**
 * Download the filtered submissions as CSV or JSON. Fetches with the auth
 * header (a plain link can't carry it) and hands the blob to the browser.
 */
export async function downloadSubmissionsExport(
  api: ApiClient,
  format: "csv" | "json",
  filters: SubmissionFilters = {}
): Promise<void> {
  const params = filterParams(filters);
  params.set("format", format);
  const response = await api.fetchRaw(`/tenant/forms/submissions/export?${params}`);
  if (!response.ok) {
    let message = `Export failed (${response.status})`;
    try {
      const body = await response.json();
      message = body?.error?.message ?? message;
    } catch {
      // Non-JSON error body — keep the status message.
    }
    throw new Error(message);
  }

  const disposition = response.headers.get("content-disposition") ?? "";
  const filename = /filename="([^"]+)"/.exec(disposition)?.[1] ?? `submissions.${format}`;
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

/** A stored file value inside submission data. */
export function submissionFileValue(
  value: unknown
): { key: string; name: string; size: number; type: string } | null {
  const file = (value as { file?: { key?: unknown } })?.file;
  return file && typeof file.key === "string"
    ? (file as { key: string; name: string; size: number; type: string })
    : null;
}
